import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth/useAuth';

export interface Circle {
  id: string;
  name: string;
  care_recipient_id: string;
  subscription_plan: 'free' | 'family' | 'pro';
  created_by: string;
}

export interface CircleContextValue {
  circles: Circle[];
  currentCircle: Circle | null;
  setCurrentCircleId: (id: string) => void;
  loading: boolean;
  refresh: () => Promise<void>;
}

const CURRENT_CIRCLE_KEY = 'careos.currentCircleId';

export const CircleContext = createContext<CircleContextValue | undefined>(undefined);

export function CircleProvider({ children }: { children: ReactNode }): JSX.Element {
  const { user } = useAuth();
  const [circles, setCircles] = useState<Circle[]>([]);
  const [currentCircleId, setCurrentCircleIdState] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(CURRENT_CIRCLE_KEY);
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setCircles([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from('care_circles')
      .select('id, name, care_recipient_id, subscription_plan, created_by')
      .order('created_at', { ascending: true });
    if (!error && data) {
      setCircles(data as Circle[]);
      if (data.length > 0 && (!currentCircleId || !data.find((c) => c.id === currentCircleId))) {
        const first = data[0];
        if (first) {
          setCurrentCircleIdState(first.id);
          try {
            sessionStorage.setItem(CURRENT_CIRCLE_KEY, first.id);
          } catch {
            /* ignore */
          }
        }
      }
    }
    setLoading(false);
  }, [user, currentCircleId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setCurrentCircleId = useCallback((id: string) => {
    setCurrentCircleIdState(id);
    try {
      sessionStorage.setItem(CURRENT_CIRCLE_KEY, id);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo<CircleContextValue>(() => {
    const currentCircle = circles.find((c) => c.id === currentCircleId) ?? circles[0] ?? null;
    return { circles, currentCircle, setCurrentCircleId, loading, refresh };
  }, [circles, currentCircleId, setCurrentCircleId, loading, refresh]);

  return <CircleContext.Provider value={value}>{children}</CircleContext.Provider>;
}
